import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import ReportPdfColorModal from "../ReportPdfColorModal";
import axios from "axios";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import {
  AssessmentOutlined,
  Close,
  FilterAltOutlined,
  RestartAlt,
} from "@mui/icons-material";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { usePdfSettings, DEFAULT_PDF_COLORS } from "../../../layouts/PdfSettingsContext";
import { useBranch } from "../../../layouts/BranchContext.js";

/* =========================================================
   BASE API
========================================================= */

const BASE_URL = "https://wims.revisetoexcel.com/api";

const API = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

/* =========================================================
   AUTH TOKEN
========================================================= */

const getAuthToken = () => {
  return (
    sessionStorage.getItem("accessToken") ||
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("access_token") ||
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("token") ||
    localStorage.getItem("token") ||
    ""
  );
};

/* =========================================================
   AXIOS JWT INTERCEPTOR
========================================================= */

API.interceptors.request.use(
  (config) => {
    const token = getAuthToken();

    if (token) {
      config.headers = config.headers || {};

      config.headers.Authorization = token.startsWith(
        "Bearer "
      )
        ? token
        : `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* =========================================================
   API ENDPOINTS
========================================================= */

const BRANCH_DETAILS_API = (branchId) =>
  `/master/branch/${branchId}/`;

const COURSE_REPORT_API =
  "/reports/course-report/";

const COURSE_REPORT_EXCEL_API =
  "/reports/reports/course-tracker/export/excel/";

/* =========================================================
   HELPERS
========================================================= */

const normalizeArray = (value) => {
  if (Array.isArray(value)) return value;

  if (Array.isArray(value?.data)) {
    return value.data;
  }

  if (Array.isArray(value?.results)) {
    return value.results;
  }

  if (Array.isArray(value?.items)) {
    return value.items;
  }

  if (Array.isArray(value?.rows)) {
    return value.rows;
  }

  return [];
};

const getUser = () => {
  for (const key of [
    "user",
    "userData",
    "currentUser",
  ]) {
    try {
      const raw =
        sessionStorage.getItem(key) ||
        localStorage.getItem(key);

      if (raw) {
        return JSON.parse(raw);
      }
    } catch {}
  }

  return {};
};

const getTodayString = () => {
  const date = new Date();

  const year = date.getFullYear();

  const month = String(
    date.getMonth() + 1
  ).padStart(2, "0");

  const day = String(
    date.getDate()
  ).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getOneMonthAgoString = () => {
  const date = new Date();

  date.setMonth(
    date.getMonth() - 1
  );

  const year = date.getFullYear();

  const month = String(
    date.getMonth() + 1
  ).padStart(2, "0");

  const day = String(
    date.getDate()
  ).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getDefaultDateFilters = () => ({
  date_from: getOneMonthAgoString(),
  date_to: getTodayString(),
});

const getId = (item) =>
  item?.id ??
  item?.value ??
  item?.branch_id ??
  item?.organization_id ??
  item?.trainer_id ??
  item?.batch_id ??
  item?.course_id;

const getName = (
  item,
  fallback = "—"
) =>
  item?.name ||
  item?.full_name ||
  item?.branch_name ||
  item?.organization_name ||
  item?.trainer_name ||
  item?.batch_name ||
  item?.course_name ||
  item?.label ||
  item?.title ||
  fallback;

const labelize = (key) =>
  String(key)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) =>
      c.toUpperCase()
    );

const formatValue = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "—";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (Array.isArray(value)) {
    return value
      .map(formatValue)
      .join(", ");
  }

  if (typeof value === "object") {
    return Object.entries(value)
      .map(
        ([key, value]) =>
          `${key}: ${formatValue(value)}`
      )
      .join(" | ");
  }

  return String(value);
};

const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString(
    "en-GB"
  );
};

const toDateOnlyString = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value).slice(0, 10);
  }

  return `${date.getFullYear()}-${String(
    date.getMonth() + 1
  ).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;
};

const errorMessage = (error) => {
  const status =
    error?.response?.status;

  const data =
    error?.response?.data;

  if (status === 401) {
    return "Authentication failed. Please login again.";
  }

  if (status === 403) {
    return "You do not have permission to access this report.";
  }

  if (status === 404) {
    return "Course Tracker API endpoint was not found.";
  }

  if (typeof data === "string") {
    return data;
  }

  if (data?.detail) {
    return data.detail;
  }

  if (data?.message) {
    return data.message;
  }

  if (data?.error) {
    return data.error;
  }

  if (data?.errors) {
    return Object.entries(data.errors)
      .map(
        ([key, value]) =>
          `${labelize(key)}: ${formatValue(
            value
          )}`
      )
      .join(" | ");
  }

  return (
    error?.message ||
    "Something went wrong while loading the Course Tracker report."
  );
};

/* =========================================================
   COLOR HELPERS
========================================================= */

const normalizeHex = (
  value,
  fallback = "#003366"
) => {
  if (!value) return fallback;

  const stringValue = String(
    value
  ).trim();

  if (
    /^#[0-9a-fA-F]{6}$/.test(
      stringValue
    )
  ) {
    return stringValue;
  }

  if (
    /^#[0-9a-fA-F]{3}$/.test(
      stringValue
    )
  ) {
    return (
      "#" +
      stringValue
        .slice(1)
        .split("")
        .map((char) => char + char)
        .join("")
    );
  }

  return fallback;
};

const hexToRgb = (hex) => {
  if (!hex) {
    return [0, 0, 0];
  }

  let value = String(hex).replace(
    "#",
    ""
  );

  if (value.length === 3) {
    value = value
      .split("")
      .map((char) => char + char)
      .join("");
  }

  if (value.length !== 6) {
    return [0, 0, 0];
  }

  return [
    parseInt(
      value.slice(0, 2),
      16
    ) || 0,
    parseInt(
      value.slice(2, 4),
      16
    ) || 0,
    parseInt(
      value.slice(4, 6),
      16
    ) || 0,
  ];
};

/* =========================================================
   DYNAMIC IMAGE URL
========================================================= */

const getAbsoluteImageUrl = (
  image
) => {
  if (!image) return "";

  const value = String(
    image
  ).trim();

  if (!value) return "";

  if (
    value.startsWith(
      "data:image"
    )
  ) {
    return value;
  }

  if (
    value.startsWith(
      "http://"
    ) ||
    value.startsWith(
      "https://"
    )
  ) {
    return value;
  }

  if (value.startsWith("//")) {
    return `${window.location.protocol}${value}`;
  }

  const API_ORIGIN =
    BASE_URL.replace(
      /\/api\/?$/,
      ""
    );

  if (value.startsWith("/")) {
    return `${API_ORIGIN}${value}`;
  }

  return `${API_ORIGIN}/${value}`;
};

/* =========================================================
   IMAGE BLOB -> DATA URL
========================================================= */

const blobToDataUrl = (
  blob
) =>
  new Promise(
    (resolve, reject) => {
      const reader =
        new FileReader();

      reader.onloadend = () =>
        resolve(
          reader.result
        );

      reader.onerror =
        reject;

      reader.readAsDataURL(
        blob
      );
    }
  );

/* =========================================================
   DATA IMAGE -> PNG DATA URL
========================================================= */

const convertDataUrlToPng = (
  dataUrl
) =>
  new Promise(
    (resolve, reject) => {
      if (
        !dataUrl ||
        !dataUrl.startsWith(
          "data:image/"
        )
      ) {
        resolve("");
        return;
      }

      if (
        dataUrl.startsWith(
          "data:image/png"
        )
      ) {
        resolve(dataUrl);
        return;
      }

      const img =
        new Image();

      img.onload = () => {
        try {
          const canvas =
            document.createElement(
              "canvas"
            );

          const width =
            img.naturalWidth ||
            500;

          const height =
            img.naturalHeight ||
            500;

          canvas.width =
            width;

          canvas.height =
            height;

          const context =
            canvas.getContext(
              "2d"
            );

          context.clearRect(
            0,
            0,
            width,
            height
          );

          context.drawImage(
            img,
            0,
            0,
            width,
            height
          );

          resolve(
            canvas.toDataURL(
              "image/png"
            )
          );
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () =>
        reject(
          new Error(
            "Unable to convert image to PNG."
          )
        );

      img.src = dataUrl;
    }
  );

/* =========================================================
   IMAGE URL -> PDF READY DATA URL
========================================================= */

const imageUrlToDataUrl =
  async (imageUrl) => {
    if (!imageUrl) {
      return "";
    }

    if (
      imageUrl.startsWith(
        "data:image"
      )
    ) {
      try {
        return await convertDataUrlToPng(
          imageUrl
        );
      } catch (error) {
        console.error(
          "Data image conversion error:",
          error
        );

        return "";
      }
    }

    try {
      const response =
        await API.get(
          imageUrl,
          {
            responseType:
              "blob",
          }
        );

      const dataUrl =
        await blobToDataUrl(
          response.data
        );

      return await convertDataUrlToPng(
        dataUrl
      );
    } catch (error) {
      console.error(
        "Logo image request failed:",
        error
      );

      return "";
    }
  };

/* =========================================================
   COMPONENT
========================================================= */

export default function CourseTrackerReport() {
  const user = useMemo(
    () => getUser(),
    []
  );

  const {
    selectedBranchId,
    branchList: globalBranchList = [],
    organizationId: globalOrganizationId,
  } = useBranch();

  const organizationId =
    globalOrganizationId ||
    user?.organization_id ||
    user?.organization?.id ||
    "";

  const organizationName =
    user?.organization_name ||
    user?.organizationName ||
    user?.organization?.name ||
    "Organization";

  /* =======================================================
     DEFAULT FILTERS
  ======================================================= */

  const getInitialFilters =
    useCallback(() => {
      const defaultDates =
        getDefaultDateFilters();

      return {
        organization: organizationId,
        branch: selectedBranchId || "",
        trainer: "",
        batch: "",
        course: "",
        date_from: defaultDates.date_from,
        date_to: defaultDates.date_to,
      };
    }, [organizationId, selectedBranchId]);

  const [filters, setFilters] = useState(
    getInitialFilters
  );

  /* =======================================================
     DATA STATES
  ======================================================= */

  const [
    rows,
    setRows,
  ] = useState([]);

  const [
    summary,
    setSummary,
  ] = useState(null);

  const [
    trainerOptions,
    setTrainerOptions,
  ] = useState([]);

  const [
    batchOptions,
    setBatchOptions,
  ] = useState([]);

  const [
    courseOptions,
    setCourseOptions,
  ] = useState([]);

  /* =======================================================
     DYNAMIC BRANCH DATA
  ======================================================= */

  const [
    branchDetails,
    setBranchDetails,
  ] = useState(null);

  const [
    branchLogo,
    setBranchLogo,
  ] = useState("");

  const [
    branchName,
    setBranchName,
  ] = useState("");

  /* =======================================================
     UI STATES
  ======================================================= */

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    pdfLoading,
    setPdfLoading,
  ] = useState(false);

  const [
    excelLoading,
    setExcelLoading,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const [
    search,
    setSearch,
  ] = useState("");

  const [
    page,
    setPage,
  ] = useState(0);

  const [
    rowsPerPage,
    setRowsPerPage,
  ] = useState(10);

  const [
    filterOpen,
    setFilterOpen,
  ] = useState(false);

  const [
    colorOpen,
    setColorOpen,
  ] = useState(false);

  const [
    snackbar,
    setSnackbar,
  ] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  /* =======================================================
     PDF SETTINGS
  ======================================================= */

  const {
    pdfThemeColor,
    pdfTextColor,
    updatePdfThemeColor,
    updatePdfTextColor,
  } = usePdfSettings();

  const [
    tempThemeColor,
    setTempThemeColor,
  ] = useState(
    normalizeHex(
      pdfThemeColor,
      DEFAULT_PDF_COLORS.primary
    )
  );

  const [
    tempTextColor,
    setTempTextColor,
  ] = useState(
    normalizeHex(
      pdfTextColor,
      DEFAULT_PDF_COLORS.bodyText
    )
  );

  /* =======================================================
     NOTIFICATION
  ======================================================= */

  const notify = useCallback(
    (
      message,
      severity = "success"
    ) => {
      setSnackbar({
        open: true,
        message,
        severity,
      });
    },
    []
  );

  /* =======================================================
     FETCH SELECTED BRANCH DETAILS
     
     /master/branch/{branchId}/
  ======================================================= */

  const fetchBranchDetails =
    useCallback(
      async (branchId) => {
        if (!branchId) {
          setBranchDetails(null);
          setBranchLogo("");
          setBranchName("");

          return null;
        }

        try {
          console.log(
            "🔥 MASTER BRANCH API CALL:",
            BRANCH_DETAILS_API(
              branchId
            )
          );

          const response =
            await API.get(
              BRANCH_DETAILS_API(
                branchId
              )
            );

          console.log(
            "🔥 MASTER BRANCH RESPONSE:",
            response.data
          );

          const data =
            response?.data || {};

          setBranchDetails(data);

          const dynamicBranchName =
            data?.name ||
            data?.branch_name ||
            data?.branchName ||
            data?.title ||
            "";

          setBranchName(
            dynamicBranchName
          );

          const image =
            data?.image ||
            data?.logo ||
            data?.branch_logo ||
            data?.branchLogo ||
            "";

          const absoluteImageUrl =
            getAbsoluteImageUrl(
              image
            );

          console.log(
            "🔥 Branch image from API:",
            image
          );

          console.log(
            "🔥 Absolute branch image:",
            absoluteImageUrl
          );

          setBranchLogo(
            absoluteImageUrl
          );

          return {
            data,

            branchName:
              dynamicBranchName,

            imageUrl:
              absoluteImageUrl,
          };
        } catch (error) {
          console.error(
            "❌ MASTER BRANCH API ERROR:",
            error
          );

          setBranchDetails(null);
          setBranchLogo("");
          setBranchName("");

          return null;
        }
      },
      []
    );

  /* =======================================================
     SELECTED BRANCH EFFECT
     
     Selected branch =>
     dynamic name + logo
  ======================================================= */

  useEffect(() => {
    if (selectedBranchId) {
      fetchBranchDetails(
        selectedBranchId
      );
    } else {
      setBranchDetails(null);
      setBranchLogo("");
      setBranchName("");
    }
  }, [
    selectedBranchId,
    fetchBranchDetails,
  ]);

  /* =======================================================
     PDF COLOR SYNC
  ======================================================= */

  useEffect(() => {
    if (colorOpen) {
      setTempThemeColor(
        normalizeHex(
          pdfThemeColor,
          DEFAULT_PDF_COLORS.primary
        )
      );

      setTempTextColor(
        normalizeHex(
          pdfTextColor,
          DEFAULT_PDF_COLORS.bodyText
        )
      );
    }
  }, [
    colorOpen,
    pdfThemeColor,
    pdfTextColor,
  ]);

  /* =======================================================
     REPORT PAYLOAD
  ======================================================= */

  const buildReportPayload = useCallback(
    (currentFilters = filters) => {
      const payload = {
        fromDate: currentFilters.date_from || "",
        toDate: currentFilters.date_to || "",
        organizationId:
          globalOrganizationId ||
          currentFilters.organization ||
          organizationId ||
          "",
        branch:
          selectedBranchId ||
          currentFilters.branch ||
          "",
      };

      if (currentFilters.trainer) {
        payload.trainer = Number.isNaN(
          Number(currentFilters.trainer)
        )
          ? currentFilters.trainer
          : Number(currentFilters.trainer);
      }

      return payload;
    },
    [
      filters,
      globalOrganizationId,
      organizationId,
      selectedBranchId,
    ]
  );

  /* =======================================================
     REPORT API - POST
  ======================================================= */

  const fetchCourseReport = useCallback(
    async (currentFilters = filters) => {
      const payload = buildReportPayload(
        currentFilters
      );

      if (!payload.branch) {
        return [];
      }

      const response = await API.post(
        COURSE_REPORT_API,
        payload
      );

      return normalizeArray(
        response?.data?.data ??
          response?.data?.results ??
          response?.data?.rows ??
          response?.data
      );
    },
    [buildReportPayload, filters]
  );

  /* =======================================================
     BUILD DROPDOWN OPTIONS
  ======================================================= */

  const buildDropdownOptions =
    useCallback(
      (data) => {
        const trainerMap =
          new Map();

        const batchMap =
          new Map();

        const courseMap =
          new Map();

        data.forEach((row) => {
          const trainerId =
            row?.trainer_id ??
            row?.trainer?.id;

          const trainerName =
            row?.trainer_name ??
            row?.trainer?.name;

          if (
            trainerId != null
          ) {
            trainerMap.set(
              String(
                trainerId
              ),
              {
                value:
                  trainerId,

                label:
                  trainerName ||
                  `Trainer ${trainerId}`,
              }
            );
          }

          const batchId =
            row?.batch_id ??
            row?.batch?.id;

          const batchName =
            row?.batch_name ??
            row?.batch?.name;

          if (
            batchId != null
          ) {
            batchMap.set(
              String(
                batchId
              ),
              {
                value:
                  batchId,

                label:
                  batchName ||
                  `Batch ${batchId}`,

                trainerId,
              }
            );
          }

          const courseId =
            row?.course_id ??
            row?.course?.id;

          const courseName =
            row?.course_name ??
            row?.course?.name;

          if (
            courseId != null
          ) {
            courseMap.set(
              String(
                courseId
              ),
              {
                value:
                  courseId,

                label:
                  courseName ||
                  `Course ${courseId}`,

                trainerId,

                batchId,
              }
            );
          }
        });

        setTrainerOptions([
          ...trainerMap.values(),
        ]);

        setBatchOptions([
          ...batchMap.values(),
        ]);

        setCourseOptions([
          ...courseMap.values(),
        ]);
      },
      []
    );

  /* =======================================================
     CLIENT FILTERS
  ======================================================= */

  const applyClientFilters =
    useCallback(
      (
        data,
        currentFilters
      ) => {
        return data.filter(
          (row) => {
            if (
              currentFilters.trainer
            ) {
              const trainerId =
                row?.trainer_id ??
                row?.trainer?.id;

              if (
                String(
                  trainerId
                ) !==
                String(
                  currentFilters.trainer
                )
              ) {
                return false;
              }
            }

            if (
              currentFilters.batch
            ) {
              const batchId =
                row?.batch_id ??
                row?.batch?.id;

              if (
                String(
                  batchId
                ) !==
                String(
                  currentFilters.batch
                )
              ) {
                return false;
              }
            }

            if (
              currentFilters.course
            ) {
              const courseId =
                row?.course_id ??
                row?.course?.id;

              if (
                String(
                  courseId
                ) !==
                String(
                  currentFilters.course
                )
              ) {
                return false;
              }
            }

            const rowDate =
              toDateOnlyString(
                row?.date ??
                  row?.course_date ??
                  row?.created_at ??
                  row?.createdAt
              );

            if (
              currentFilters.date_from &&
              rowDate
            ) {
              if (
                rowDate <
                currentFilters.date_from
              ) {
                return false;
              }
            }

            if (
              currentFilters.date_to &&
              rowDate
            ) {
              if (
                rowDate >
                currentFilters.date_to
              ) {
                return false;
              }
            }

            return true;
          }
        );
      },
      []
    );

  /* =======================================================
     LOAD REPORT
     Global branch only.
  ======================================================= */

  const loadReport = useCallback(
    async (currentFilters = filters) => {
      try {
        setLoading(true);
        setError("");

        const branchId =
          selectedBranchId ||
          currentFilters.branch;

        if (!branchId) {
          setRows([]);
          setSummary(null);
          setTrainerOptions([]);
          setBatchOptions([]);
          setCourseOptions([]);
          return [];
        }

        const data = await fetchCourseReport({
          ...currentFilters,
          branch: branchId,
        });

        buildDropdownOptions(data);

        const filteredData =
          applyClientFilters(data, {
            ...currentFilters,
            branch: branchId,
          });

        setRows(filteredData);

        const currentBranchName =
          branchName ||
          globalBranchList.find(
            (branch) =>
              String(getId(branch)) ===
              String(branchId)
          )?.name ||
          globalBranchList.find(
            (branch) =>
              String(getId(branch)) ===
              String(branchId)
          )?.branch_name ||
          `Branch ${branchId}`;

        setSummary({
          total_records: filteredData.length,
          total_branches: 1,
          organization: organizationName,
          branch: currentBranchName,
        });

        setPage(0);

        return filteredData;
      } catch (error) {
        console.error(
          "Course Tracker report error:",
          error
        );

        const message = errorMessage(error);
        setRows([]);
        setSummary(null);
        setError(message);
        notify(message, "error");
        return [];
      } finally {
        setLoading(false);
      }
    },
    [
      filters,
      selectedBranchId,
      fetchCourseReport,
      buildDropdownOptions,
      applyClientFilters,
      branchName,
      globalBranchList,
      organizationName,
      notify,
    ]
  );

  /* =======================================================
     GLOBAL BRANCH SYNC
  ======================================================= */

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      organization:
        globalOrganizationId ||
        organizationId ||
        prev.organization ||
        "",
      branch: selectedBranchId || "",
    }));

    setRows([]);
    setSummary(null);
    setPage(0);
    setTrainerOptions([]);
    setBatchOptions([]);
    setCourseOptions([]);

    if (selectedBranchId) {
      loadReport({
        ...getInitialFilters(),
        organization:
          globalOrganizationId ||
          organizationId ||
          "",
        branch: selectedBranchId,
      });
    }
  }, [
    selectedBranchId,
    globalOrganizationId,
  ]);

  /* =======================================================
     CHANGE FILTER
  ======================================================= */

  const changeFilter = (
    name,
    value
  ) => {
    setFilters((prev) => {
      const next = {
        ...prev,
        [name]: value,
      };

      if (
        name === "trainer"
      ) {
        next.batch = "";
        next.course = "";
      }

      if (
        name === "batch"
      ) {
        next.course = "";
      }

      return next;
    });

    setPage(0);
  };

  /* =======================================================
     FILTERED OPTIONS
  ======================================================= */

  const filteredBatchOptions =
    useMemo(() => {
      if (!filters.trainer) {
        return batchOptions;
      }

      return batchOptions.filter(
        (option) =>
          String(
            option.trainerId
          ) ===
          String(
            filters.trainer
          )
      );
    }, [
      batchOptions,
      filters.trainer,
    ]);

  const filteredCourseOptions =
    useMemo(() => {
      return courseOptions.filter(
        (option) => {
          if (
            filters.trainer &&
            String(
              option.trainerId
            ) !==
              String(
                filters.trainer
              )
          ) {
            return false;
          }

          if (
            filters.batch &&
            String(
              option.batchId
            ) !==
              String(
                filters.batch
              )
          ) {
            return false;
          }

          return true;
        }
      );
    }, [
      courseOptions,
      filters.trainer,
      filters.batch,
    ]);

  /* =======================================================
     GENERATE REPORT
  ======================================================= */

  const generateReport = async () => {
    const branchId = selectedBranchId;

    if (!branchId) {
      notify(
        "Please select a branch from the global branch selector.",
        "warning"
      );
      return;
    }

    if (
      filters.date_from &&
      filters.date_to &&
      filters.date_from > filters.date_to
    ) {
      notify(
        "Date From cannot be greater than Date To.",
        "error"
      );
      return;
    }

    const nextFilters = {
      ...filters,
      organization:
        globalOrganizationId ||
        organizationId ||
        filters.organization ||
        "",
      branch: branchId,
    };

    setFilters(nextFilters);

    const generatedRows =
      await loadReport(nextFilters);

    setFilterOpen(false);

    if (generatedRows?.length) {
      setTempThemeColor(
        normalizeHex(
          pdfThemeColor,
          DEFAULT_PDF_COLORS.primary
        )
      );

      setTempTextColor(
        normalizeHex(
          pdfTextColor,
          DEFAULT_PDF_COLORS.bodyText
        )
      );

      setColorOpen(true);

      notify(
        "Course Tracker report generated successfully."
      );
    }
  };

  /* =======================================================
     RESET
  ======================================================= */

  const reset = () => {
    const defaultDates = getDefaultDateFilters();

    const nextFilters = {
      organization:
        globalOrganizationId ||
        organizationId ||
        "",
      branch: selectedBranchId || "",
      trainer: "",
      batch: "",
      course: "",
      date_from: defaultDates.date_from,
      date_to: defaultDates.date_to,
    };

    setFilters(nextFilters);
    setSearch("");
    setPage(0);
    setError("");
    setSummary(null);
    setRows([]);
    setTrainerOptions([]);
    setBatchOptions([]);
    setCourseOptions([]);
    setFilterOpen(false);

    notify("Filters reset successfully.");
  };

  /* =======================================================
     SEARCH
  ======================================================= */

  const filteredRows =
    useMemo(() => {
      const query =
        search
          .trim()
          .toLowerCase();

      if (!query) {
        return rows;
      }

      return rows.filter(
        (row) =>
          Object.values(
            row || {}
          ).some((value) =>
            formatValue(value)
              .toLowerCase()
              .includes(query)
          )
      );
    }, [
      rows,
      search,
    ]);

  /* =======================================================
     COLUMNS
  ======================================================= */

  const columns =
    useMemo(() => {
      const preferred = [
        "branch_name",
        "trainer_name",
        "course_name",
        "batch_name",
        "status",
        "date",
        "remark",
      ];

      if (!rows.length) {
        return preferred;
      }

      const keys =
        new Set();

      rows.forEach(
        (row) => {
          Object.keys(
            row || {}
          ).forEach((key) =>
            keys.add(key)
          );
        }
      );

      return [
        ...preferred.filter(
          (key) =>
            keys.has(key)
        ),

        ...Array.from(
          keys
        ).filter(
          (key) =>
            !preferred.includes(
              key
            )
        ),
      ];
    }, [rows]);

  /* =======================================================
     PAGINATION
  ======================================================= */

  const paginatedRows =
    useMemo(() => {
      return filteredRows.slice(
        page *
          rowsPerPage,

        page *
            rowsPerPage +
          rowsPerPage
      );
    }, [
      filteredRows,
      page,
      rowsPerPage,
    ]);

  useEffect(() => {
    const maxPage =
      Math.max(
        0,
        Math.ceil(
          filteredRows.length /
            rowsPerPage
        ) - 1
      );

    if (
      page > maxPage
    ) {
      setPage(maxPage);
    }
  }, [
    filteredRows.length,
    rowsPerPage,
    page,
  ]);

  /* =======================================================
     ACTIVE FILTER COUNT
  ======================================================= */

  const activeCount =
    Object.entries(
      filters
    ).filter(
      ([key, value]) =>
        key !==
          "organization" &&
        key !==
          "branch" &&
        value !== "" &&
        value !== null &&
        value !== undefined
    ).length;

  /* =======================================================
     SELECTED GLOBAL BRANCH LABEL
  ======================================================= */

  const selectedBranchLabel = useMemo(() => {
    if (!selectedBranchId) {
      return "Select Branch";
    }

    if (branchName) {
      return branchName;
    }

    const selected = globalBranchList.find(
      (branch) =>
        String(getId(branch)) ===
        String(selectedBranchId)
    );

    return getName(
      selected,
      `Branch ${selectedBranchId}`
    );
  }, [
    selectedBranchId,
    branchName,
    globalBranchList,
  ]);

  /* =======================================================
     DISPLAY
  ======================================================= */

  const display = (
    row,
    column
  ) => {
    if (
      column.includes(
        "date"
      ) ||
      column.endsWith(
        "_from"
      ) ||
      column.endsWith(
        "_to"
      )
    ) {
      return formatDate(
        row?.[column]
      );
    }

    return formatValue(
      row?.[column]
    );
  };

  /* =======================================================
     PDF HEADER
  ======================================================= */

  const drawPdfHeader = (
    doc,
    pdfLogo,
    pdfOrganizationName,
    pdfBranchName,
    theme
  ) => {
    const pageWidth =
      doc.internal.pageSize.getWidth();

    const margin = 10;

    const primaryColor =
      hexToRgb(theme);

    doc.setFillColor(
      ...primaryColor
    );

    doc.rect(
      0,
      0,
      pageWidth,
      36,
      "F"
    );

    if (pdfLogo) {
      try {
        doc.addImage(
          pdfLogo,
          "PNG",
          margin,
          5,
          34,
          24
        );
      } catch (error) {
        console.error(
          "PDF dynamic logo add error:",
          error
        );
      }
    }

    doc.setFont(
      "helvetica",
      "bold"
    );

    doc.setFontSize(17);

    doc.setTextColor(
      ...hexToRgb(
        DEFAULT_PDF_COLORS.headerText
      )
    );

    doc.text(
      pdfOrganizationName ||
        organizationName ||
        "Organization",
      pageWidth / 2,
      12,
      {
        align: "center",
      }
    );

    doc.setFontSize(11);

    doc.text(
      pdfBranchName ||
        "Branch",
      pageWidth / 2,
      19,
      {
        align: "center",
      }
    );

    doc.setFont(
      "helvetica",
      "normal"
    );

    doc.setFontSize(10);

    doc.text(
      "Course Tracker Report",
      pageWidth / 2,
      26,
      {
        align: "center",
      }
    );

    doc.setFontSize(7.5);

    doc.text(
      `Generated: ${new Date().toLocaleString()}`,
      pageWidth - margin,
      32,
      {
        align: "right",
      }
    );
  };

  /* =======================================================
     CREATE COURSE TRACKER PDF
  ======================================================= */

  const createCourseTrackerPdf =
    async (
      reportRows,
      themeColor,
      textColorValue,
      reportMetadata = {}
    ) => {
      const doc =
        new jsPDF({
          orientation:
            "landscape",

          unit: "mm",

          format: "a4",
        });

      const pageWidth =
        doc.internal.pageSize.getWidth();

      const pageHeight =
        doc.internal.pageSize.getHeight();

      const margin = 10;

      const primaryColor =
        hexToRgb(
          themeColor
        );

      const pdfTextColor =
        hexToRgb(
          textColorValue
        );

      /* =========================================
         SELECTED BRANCH DETAILS
      ========================================= */

      let currentBranchName =
        branchName;

      let currentBranchLogo =
        branchLogo;

      if (
        selectedBranchId &&
        (!currentBranchName ||
          !currentBranchLogo)
      ) {
        const branchResult =
          await fetchBranchDetails(
            selectedBranchId
          );

        currentBranchName =
          branchResult
            ?.branchName ||
          currentBranchName ||
          "";

        currentBranchLogo =
          branchResult
            ?.imageUrl ||
          currentBranchLogo ||
          "";
      }

      /* =========================================
         FALLBACK BRANCH NAME
      ========================================= */

      if (
        !currentBranchName &&
        selectedBranchId
      ) {
        const selectedBranch =
          globalBranchList.find(
            (branch) =>
              String(
                getId(branch)
              ) ===
              String(
                selectedBranchId
              )
          );

        currentBranchName =
          selectedBranch
            ?.name ||
          selectedBranch
            ?.branch_name ||
          selectedBranch
            ?.branchName ||
          "";
      }

      if (!currentBranchName) {
        currentBranchName =
          "Branch";
      }

      /* =========================================
         DYNAMIC LOGO -> PNG
      ========================================= */

      let pdfLogo = "";

      if (
        currentBranchLogo
      ) {
        console.log(
          "🔥 Preparing dynamic PDF logo:",
          currentBranchLogo
        );

        pdfLogo =
          await imageUrlToDataUrl(
            currentBranchLogo
          );

        console.log(
          "🔥 Dynamic PDF logo ready:",
          Boolean(pdfLogo)
        );
      }

      /* =========================================
         DYNAMIC ORGANIZATION NAME
      ========================================= */

      const currentOrganizationName =
        reportMetadata?.organization_name ||
        reportRows?.find(
          (row) =>
            row?.organization_name ||
            row?.organizationName
        )?.organization_name ||
        reportRows?.find(
          (row) =>
            row?.organization_name ||
            row?.organizationName
        )?.organizationName ||
        organizationName ||
        "Organization";

      /* =========================================
         HEADER
      ========================================= */

      drawPdfHeader(
        doc,
        pdfLogo,
        currentOrganizationName,
        currentBranchName,
        themeColor
      );

      let y = 44;

      /* =========================================
         FILTERS
      ========================================= */

      doc.setTextColor(
        ...pdfTextColor
      );

      doc.setFont(
        "helvetica",
        "bold"
      );

      doc.setFontSize(9);

      doc.text(
        "FILTERS",
        margin,
        y
      );

      y += 5;

      doc.setFont(
        "helvetica",
        "normal"
      );

      doc.setFontSize(7.5);

      const filterText =
        Object.entries(
          filters
        )
          .filter(
            ([, value]) =>
              value !== "" &&
              value !== null &&
              value !== undefined
          )
          .map(
            ([key, value]) =>
              `${labelize(
                key
              )}: ${formatValue(
                value
              )}`
          )
          .join(
            "   |   "
          ) ||
        "No filters";

      const filterLines =
        doc.splitTextToSize(
          filterText,
          pageWidth -
            margin * 2
        );

      doc.text(
        filterLines,
        margin,
        y
      );

      y +=
        filterLines.length *
          4 +
        5;

      /* =========================================
         SUMMARY
      ========================================= */

      if (summary) {
        const summaryEntries =
          Object.entries(
            summary
          ).filter(
            ([, value]) =>
              value !== null &&
              value !==
                undefined &&
              typeof value !==
                "object"
          );

        if (
          summaryEntries.length
        ) {
          doc.setFont(
            "helvetica",
            "bold"
          );

          doc.setFontSize(9);

          doc.text(
            "SUMMARY",
            margin,
            y
          );

          y += 5;

          doc.setFont(
            "helvetica",
            "normal"
          );

          doc.setFontSize(7.5);

          const summaryText =
            summaryEntries
              .map(
                ([key, value]) =>
                  `${labelize(
                    key
                  )}: ${formatValue(
                    value
                  )}`
              )
              .join(
                "   |   "
              );

          const summaryLines =
            doc.splitTextToSize(
              summaryText,
              pageWidth -
                margin * 2
            );

          doc.text(
            summaryLines,
            margin,
            y
          );

          y +=
            summaryLines.length *
              4 +
            5;
        }
      }

      /* =========================================
         TABLE COLUMNS
      ========================================= */

      const keys =
        new Set();

      reportRows.forEach(
        (row) => {
          Object.keys(
            row || {}
          ).forEach((key) =>
            keys.add(key)
          );
        }
      );

      const preferredColumns = [
        "branch_name",
        "trainer_name",
        "course_name",
        "batch_name",
        "status",
        "date",
        "remark",
      ];

      const tableColumns = [
        ...preferredColumns.filter(
          (key) =>
            keys.has(key)
        ),

        ...Array.from(
          keys
        ).filter(
          (key) =>
            !preferredColumns.includes(
              key
            )
        ),
      ];

      const tableRows =
        reportRows.map(
          (row) =>
            tableColumns.map(
              (column) => {
                if (
                  column.includes(
                    "date"
                  ) ||
                  column.endsWith(
                    "_from"
                  ) ||
                  column.endsWith(
                    "_to"
                  )
                ) {
                  return formatDate(
                    row?.[column]
                  );
                }

                return formatValue(
                  row?.[column]
                );
              }
            )
        );

      /* =========================================
         TABLE
      ========================================= */

      autoTable(doc, {
        startY: y,

        head: [
          tableColumns.map(
            (column) =>
              labelize(column)
          ),
        ],

        body: tableRows,

        theme: "grid",

        margin: {
          top: 42,
          left: margin,
          right: margin,
          bottom: 14,
        },

        styles: {
          font: "helvetica",
          fontSize: 7,
          cellPadding: 2,

          lineColor:
            hexToRgb(
              DEFAULT_PDF_COLORS.border
            ),

          lineWidth: 0.1,

          textColor:
            pdfTextColor,

          valign:
            "middle",

          halign:
            "center",
        },

        headStyles: {
          fillColor:
            primaryColor,

          textColor:
            hexToRgb(
              DEFAULT_PDF_COLORS.tableHeaderText
            ),

          fontStyle:
            "bold",

          fontSize: 7.5,

          halign:
            "center",

          valign:
            "middle",
        },

        alternateRowStyles: {
          fillColor:
            hexToRgb(
              DEFAULT_PDF_COLORS.alternateRow
            ),
        },

        willDrawPage: () => {
          drawPdfHeader(
            doc,
            pdfLogo,
            currentOrganizationName,
            currentBranchName,
            themeColor
          );
        },

        didDrawPage: () => {
          const currentPage =
            doc.internal.getNumberOfPages();

          doc.setFont(
            "helvetica",
            "normal"
          );

          doc.setFontSize(7);

          doc.setTextColor(
            ...hexToRgb(
              DEFAULT_PDF_COLORS.mutedText
            )
          );

          doc.text(
            "Course Tracker Report",
            margin,
            pageHeight - 5
          );

          doc.text(
            `Page ${currentPage}`,
            pageWidth -
              margin,
            pageHeight - 5,
            {
              align:
                "right",
            }
          );
        },
      });

      return doc;
    };

  /* =======================================================
     GENERATE PDF
  ======================================================= */

  const generatePDF = async (
    theme = tempThemeColor,
    text = tempTextColor
  ) => {
    try {
      setPdfLoading(true);

      if (!selectedBranchId) {
        notify(
          "Please select a branch first.",
          "warning"
        );
        return;
      }

      const currentRows = rows;

      if (!currentRows?.length) {
        notify(
          "Please generate the Course Tracker report first.",
          "warning"
        );
        return;
      }

      const reportMetadata = {
        organization_name:
          organizationName,
        branch_name:
          selectedBranchLabel,
        from_date:
          filters.date_from,
        to_date:
          filters.date_to,
      };

      const doc =
        await createCourseTrackerPdf(
          currentRows,
          normalizeHex(
            theme,
            DEFAULT_PDF_COLORS.primary
          ),
          normalizeHex(
            text,
            DEFAULT_PDF_COLORS.bodyText
          ),
          reportMetadata
        );

      const blob =
        doc.output("blob");

      const url =
        URL.createObjectURL(blob);

      const win =
        window.open(url, "_blank");

      if (!win) {
        const anchor =
          document.createElement(
            "a"
          );

        anchor.href = url;

        anchor.download =
          `course_tracker_report_${new Date()
            .toISOString()
            .slice(0, 10)}.pdf`;

        document.body.appendChild(
          anchor
        );

        anchor.click();

        anchor.remove();
      }

      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 60000);

      notify(
        "Course Tracker PDF generated successfully."
      );
    } catch (error) {
      console.error(
        "PDF generation error:",
        error
      );

      notify(
        errorMessage(error),
        "error"
      );
    } finally {
      setPdfLoading(false);
    }
  };

  /* =======================================================
     EXPORT PDF API
     POST /reports/reports/course-tracker/export/pdf/
  ======================================================= */

  /* =======================================================
     EXPORT EXCEL API
     POST /reports/reports/course-tracker/export/excel/
  ======================================================= */

  const exportExcel = async () => {
    try {
      setExcelLoading(true);

      const payload = buildReportPayload(filters);

      if (!payload.branch) {
        notify(
          "Please select a branch from the global branch selector.",
          "warning"
        );
        return;
      }

      const response = await API.post(
        COURSE_REPORT_EXCEL_API,
        payload,
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], {
        type:
          response?.headers?.["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `course_tracker_report_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);

      notify("Excel exported successfully.");
    } catch (error) {
      console.error("Excel export error:", error);
      notify(errorMessage(error), "error");
    } finally {
      setExcelLoading(false);
    }
  };

  /* =======================================================
     REFRESH
  ======================================================= */

  const refreshReport =
    async () => {
      if (!selectedBranchId) {
        notify(
          "Please select a branch first.",
          "warning"
        );

        return;
      }

      await loadReport(
        filters
      );

      notify(
        "Report refreshed successfully."
      );
    };

  /* =======================================================
     SELECT FIELD
  ======================================================= */

  const selectField = ({
    label,
    name,
    value,
    options = [],
    disabled = false,
    showAll = true,
  }) => (
    <FormControl
      fullWidth
      size="small"
      disabled={disabled}
    >
      <InputLabel>
        {label}
      </InputLabel>

      <Select
        value={value ?? ""}
        label={label}
        onChange={(event) =>
          changeFilter(
            name,
            event.target.value
          )
        }
      >
        {showAll && (
          <MenuItem value="">
            {label === "Batch"
              ? "All Batches"
              : `All ${label}s`}
          </MenuItem>
        )}

        {options
          .filter(Boolean)
          .map(
            (
              option,
              index
            ) => (
              <MenuItem
                key={`${option.value}-${index}`}
                value={
                  option.value
                }
              >
                {
                  option.label
                }
              </MenuItem>
            )
          )}
      </Select>
    </FormControl>
  );

  /* =======================================================
     GLOBAL BRANCH
     No local branch dropdown/filter.
  ======================================================= */

  /* =======================================================
     FILTER MODAL
  ======================================================= */

  const filterModal = (
    <Modal
      open={filterOpen}
      onClose={() =>
        setFilterOpen(false)
      }
      BackdropProps={{
        sx: {
          backgroundColor:
            "rgba(15, 23, 42, 0.55)",

          backdropFilter:
            "blur(3px)",
        },
      }}
    >
      <Box
        sx={{
          position:
            "absolute",

          top: "50%",
          left: "50%",

          transform:
            "translate(-50%, -50%)",

          width: {
            xs: "94%",
            sm: 620,
            md: 720,
          },

          maxHeight:
            "90vh",

          overflow:
            "hidden",

          display:
            "flex",

          flexDirection:
            "column",

          bgcolor:
            "background.paper",

          color:
            "text.primary",

          borderRadius: 3,

          boxShadow: 24,

          outline: "none",
        }}
      >
        {/* HEADER */}

        <Box
          sx={{
            px: 3,
            py: 2.2,

            display:
              "flex",

            alignItems:
              "center",

            justifyContent:
              "space-between",

            borderBottom:
              1,

            borderColor:
              "divider",
            position: "sticky",
            top: 0,
            zIndex: 3,
            backgroundColor: "background.paper",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 700,
              }}
            >
              Filter Course
              Tracker Report
            </Typography>

            <Typography
              variant="caption"
              color="text.disabled"
            >
              The global branch is used automatically for this report.
            </Typography>
          </Box>

          <IconButton
            onClick={() =>
              setFilterOpen(
                false
              )
            }
          >
            <Close />
          </IconButton>
        </Box>

        {/* FILTER FIELDS */}

        <Box
          sx={{
            p: 3,
            maxHeight: "60vh",
            overflowY: "auto",
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              display:
                "grid",

              gridTemplateColumns:
                {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                },

              gap: 2,
            }}
          >
            {/* ORGANIZATION */}

            <Box>
              <FormControl
                fullWidth
                size="small"
                disabled
              >
                <InputLabel>
                  Organization
                </InputLabel>

                <Select
                  value={
                    filters.organization ||
                    ""
                  }
                  label="Organization"
                >
                  <MenuItem
                    value={
                      filters.organization ||
                      ""
                    }
                  >
                    {organizationName ||
                      `Organization ${filters.organization}`}
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* TRAINER */}

            <Box>
              {selectField({
                label: "Trainer",

                name: "trainer",

                value:
                  filters.trainer,

                options:
                  trainerOptions,

                disabled:
                  !selectedBranchId ||
                  !trainerOptions.length,
              })}
            </Box>

            {/* BATCH */}

            <Box>
              {selectField({
                label: "Batch",

                name: "batch",

                value:
                  filters.batch,

                options:
                  filteredBatchOptions,

                disabled:
                  !selectedBranchId ||
                  !filteredBatchOptions.length,
              })}
            </Box>

            {/* COURSE */}

            <Box>
              {selectField({
                label: "Course",

                name: "course",

                value:
                  filters.course,

                options:
                  filteredCourseOptions,

                disabled:
                  !selectedBranchId ||
                  !filteredCourseOptions.length,
              })}
            </Box>

            {/* DATE FROM */}

            <TextField
              fullWidth
              size="small"
              label="Date From"
              type="date"
              value={
                filters.date_from
              }
              onChange={(event) =>
                changeFilter(
                  "date_from",
                  event.target.value
                )
              }
              InputLabelProps={{
                shrink: true,
              }}
            />

            {/* DATE TO */}

            <TextField
              fullWidth
              size="small"
              label="Date To"
              type="date"
              value={
                filters.date_to
              }
              onChange={(event) =>
                changeFilter(
                  "date_to",
                  event.target.value
                )
              }
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Box>
        </Box>

        <Divider />

        {/* FOOTER */}

        <Box
          sx={{
            px: 3,
            py: 2,

            display:
              "flex",

            justifyContent:
              "space-between",

            alignItems:
              "center",

            gap: 1,
            position: "sticky",
            bottom: 0,
            zIndex: 3,
            backgroundColor: "background.paper",
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button
            variant="text"
            startIcon={
              <RestartAlt />
            }
            onClick={reset}
            sx={{
              textTransform:
                "none",

              color:
                "text.primary",

              fontWeight: 600,
            }}
          >
            Reset
          </Button>

          <Box
            sx={{
              display:
                "flex",

              gap: 1,
            }}
          >
            <Button
              variant="contained"
              onClick={() =>
                setFilterOpen(
                  false
                )
              }
              sx={{
                textTransform:
                  "none",

                borderRadius: 2,
              }}
             data-app-cancel="true">
              Cancel
            </Button>

            <Button
              variant="contained"
              startIcon={
                <FilterAltOutlined />
              }
              disabled={
                loading ||
                !selectedBranchId
              }
              onClick={() =>
                setFilterOpen(false)
              }
              sx={{
                textTransform:
                  "none",

                borderRadius: 2,

                px: 3,

                fontWeight: 600,
              }}
            >
              Apply Filters
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );

  /* =======================================================
     RETURN
  ======================================================= */

  return (
    <Box
      sx={{
        p: {
          xs: 1.5,
          md: 3,
        },
      }}
    >
      <Card
        elevation={0}
        sx={{
          backgroundColor:
            "transparent",
        }}
      >
        <CardContent
          sx={{
            p: {
              xs: 1.5,
              md: 2.5,
            },

            "&:last-child": {
              pb: {
                xs: 1.5,
                md: 2.5,
              },
            },
          }}
        >
       {/* REPORT TAB */}

<Box
  sx={{
    display: "inline-flex",
    p: 0.5,
    mb: 2,
    borderRadius: 2,
    borderBottom: "1px solid",
    borderColor: "divider",
    overflow: "hidden",
  }}
>
  <Tabs
    value="course-tracker"
    sx={{
      minHeight: 42,

      "& .MuiTabs-indicator": {
        display: "none",
      },

      "& .MuiTab-root": {
        minHeight: 42,
        minWidth: "auto",
        px: {
          xs: 1.5,
          md: 2,
        },
        borderRadius: 1.5,
        textTransform: "none",
        fontWeight: 600,
        fontSize: {
          xs: "0.78rem",
          md: "0.86rem",
        },
        color: "text.secondary",
        opacity: 1,
        transition: "all 0.2s ease",

        "&:hover": {
          backgroundColor: "action.hover",
          color: "text.primary",
        },
      },

      "& .MuiTab-root.Mui-selected": {
        backgroundColor: "primary.main",
        color: "primary.contrastText",

        "&:hover": {
          backgroundColor: "primary.dark",
        },
      },
    }}
  >
    <Tab
      value="course-tracker"
      label="Course Tracker Report"
    />
  </Tabs>
</Box>
          {/* TOOLBAR */}

          <Box
            sx={{
              display:
                "flex",

              alignItems: {
                xs: "flex-start",
                md: "center",
              },

              justifyContent:
                "space-between",

              gap: 2,

              mt: 2,
              mb: 2,

              flexWrap:
                "wrap",
            }}
          >
            {/* SEARCH */}

            <TextField
              size="small"
              placeholder="Search report..."
              value={search}
              onChange={(event) => {
                setSearch(
                  event.target.value
                );

                setPage(0);
              }}
              sx={{
                width: {
                  xs: "100%",
                  md: 280,
                },
              }}
            />

            <Box
              sx={{
                display:
                  "flex",

                alignItems:
                  "center",

                gap: 1,

                flexWrap:
                  "wrap",

                width: {
                  xs: "100%",
                  md: "auto",
                },
              }}
            >
              <Button
                variant="contained"
                startIcon={
                  <FilterAltOutlined />
                }
                onClick={() =>
                  setFilterOpen(
                    true
                  )
                }
                sx={{
                  textTransform:
                    "none",

                  borderRadius: 2,

                  fontWeight: 600,
                }}
              >
                Filters
                {activeCount >
                  0 &&
                  ` (${activeCount})`}
              </Button>

              <Button
                variant="contained"
                startIcon={
                  loading ? (
                    <CircularProgress
                      size={17}
                      color="inherit"
                    />
                  ) : (
                    <AssessmentOutlined />
                  )
                }
                onClick={generateReport}
                disabled={
                  loading ||
                  !selectedBranchId
                }
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  fontWeight: 600,
                }}
              >
                {loading ? "Generating..." : "Generate Report"}
              </Button>

             <Button
  variant="contained"
  color="success"
  onClick={exportExcel}
  disabled={
    !selectedBranchId ||
    excelLoading
  }
  sx={{
    textTransform: "none",
    borderRadius: 2,
    fontWeight: 600,
  }}
>
  {excelLoading ? "Exporting..." : "Export Excel"}
</Button>

              <Button
                variant="contained"
                startIcon={
                  <RestartAlt />
                }
                onClick={
                  refreshReport
                }
                disabled={
                  loading ||
                  !selectedBranchId
                }
                sx={{
                  textTransform:
                    "none",

                  borderRadius: 2,
                }}
              >
                Refresh
              </Button>

              <Button
            

                variant="text"
                onClick={reset}
                sx={{
                  textTransform:
                    "none",

                  borderRadius: 2,
                }}
              >
                Reset
              </Button>
            </Box>
          </Box>

          {/* SUMMARY */}

          {summary && (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                mb: 2,

                flexWrap:
                  "wrap",

                gap: 1,
              }}
            >
              <Box
                sx={{
                  px: 1.5,
                  py: 0.7,

                  borderRadius: 2,

                  backgroundColor:
                    "action.hover",

                  fontSize:
                    ".82rem",
                }}
              >
                <strong>
                  Records:
                </strong>{" "}
                {
                  summary.total_records
                }
              </Box>

              <Box
                sx={{
                  px: 1.5,
                  py: 0.7,

                  borderRadius: 2,

                  backgroundColor:
                    "action.hover",

                  fontSize:
                    ".82rem",
                }}
              >
                <strong>
                  Branch:
                </strong>{" "}
                {
                  selectedBranchLabel
                }
              </Box>

              <Box
                sx={{
                  px: 1.5,
                  py: 0.7,

                  borderRadius: 2,

                  backgroundColor:
                    "action.hover",

                  fontSize:
                    ".82rem",
                }}
              >
                <strong>
                  From:
                </strong>{" "}
                {
                  filters.date_from
                }
              </Box>

              <Box
                sx={{
                  px: 1.5,
                  py: 0.7,

                  borderRadius: 2,

                  backgroundColor:
                    "action.hover",

                  fontSize:
                    ".82rem",
                }}
              >
                <strong>
                  To:
                </strong>{" "}
                {
                  filters.date_to
                }
              </Box>
            </Stack>
          )}

          {/* DATA */}

          {loading ? (
            <Box
              sx={{
                display:
                  "flex",

                justifyContent:
                  "center",

                py: 9,
              }}
            >
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert
              severity="error"
              variant="contained"
              sx={{
                my: 2,
                borderRadius: 2,
              }}
            >
              {error}
            </Alert>
          ) : !selectedBranchId ? (
            <Box
              sx={{
                py: 9,

                textAlign:
                  "center",
              }}
            >
              <AssessmentOutlined
                sx={{
                  fontSize: 42,

                  color:
                    "text.primary",

                  mb: 1,
                }}
              />

              <Typography
                fontWeight={600}
              >
                Select a Branch
              </Typography>

              <Typography
                variant="body2"
                color="text.disabled"
              >
                Please select a branch
                from the global Branch
                selector to generate the
                Course Tracker report.
              </Typography>
            </Box>
          ) : !filteredRows.length ? (
            <Box
              sx={{
                py: 9,

                textAlign:
                  "center",
              }}
            >
              <AssessmentOutlined
                sx={{
                  fontSize: 42,

                  color:
                    "text.primary",

                  mb: 1,
                }}
              />

              <Typography
                fontWeight={600}
              >
                No Course Tracker
                data found
              </Typography>

              <Typography
                variant="body2"
                color="text.disabled"
              >
                Try changing the
                filters or
                refreshing the
                report.
              </Typography>
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                mt: 2,

                borderRadius: 2,

                border: 1,

                borderColor:
                  "divider",

                overflowX:
                  "auto",

                backgroundColor:
                  "background.paper",
              }}
            >
              <Table
                stickyHeader
                sx={{
                  minWidth: 1000,
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: ".82rem",
                        whiteSpace: "nowrap",
                        backgroundColor: "primary.main",
                        color: "primary.contrastText",
                        py: 1.7,
                        px: 2,
                      }}
                    >
                      S.No
                    </TableCell>
                    {columns.map(
                      (column) => (
                        <TableCell
                          key={
                            column
                          }
                          sx={{
                            fontWeight: 700,

                            fontSize:
                              ".82rem",

                            whiteSpace:
                              "nowrap",

                            backgroundColor:
                              "primary.main",

                            color:
                              "primary.contrastText",

                            py: 1.7,

                            px: 2,

                            "&.MuiTableCell-stickyHeader":
                              {
                                backgroundColor:
                                  "primary.main",

                                color:
                                  "primary.contrastText",
                              },
                          }}
                        >
                          {labelize(
                            column
                          )}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {paginatedRows.map(
                    (
                      row,
                      index
                    ) => (
                      <TableRow
                        key={
                          row?.id ??
                          `${page}-${index}`
                        }
                        hover
                        sx={{
                          "&:nth-of-type(even)":
                            {
                              backgroundColor:
                                "action.hover",
                            },

                          "&:hover":
                            {
                              backgroundColor:
                                "action.selected",
                            },
                        }}
                      >
                        <TableCell
                          sx={{
                            py: 1.5,
                            px: 2,
                            fontSize: ".84rem",
                            whiteSpace: "nowrap",
                            borderColor: "divider",
                            color: "text.primary",
                          }}
                        >
                          {page * rowsPerPage + index + 1}
                        </TableCell>
                        {columns.map(
                          (
                            column
                          ) => (
                            <TableCell
                              key={
                                column
                              }
                              sx={{
                                py: 1.5,

                                px: 2,

                                fontSize:
                                  ".84rem",

                                whiteSpace:
                                  "nowrap",

                                borderColor:
                                  "divider",

                                color:
                                  "text.primary",
                              }}
                            >
                              {display(
                                row,
                                column
                              )}
                            </TableCell>
                          )
                        )}
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>

              <TablePagination
                component="div"
                count={
                  filteredRows.length
                }
                page={page}
                onPageChange={(
                  _,
                  nextPage
                ) =>
                  setPage(
                    nextPage
                  )
                }
                rowsPerPage={
                  rowsPerPage
                }
                onRowsPerPageChange={(
                  event
                ) => {
                  setRowsPerPage(
                    parseInt(
                      event.target
                        .value,
                      10
                    )
                  );

                  setPage(0);
                }}
                rowsPerPageOptions={[
                  10,
                  25,
                  50,
                  100,
                ]}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* =====================================================
          FILTER MODAL
      ====================================================== */}

      {filterModal}

      {/* =====================================================
          PDF COLOR MODAL
      ====================================================== */}

      <ReportPdfColorModal
  open={colorOpen}
  onClose={() =>
          setColorOpen(
            false
          )}
  themeColor={tempThemeColor}
  setThemeColor={(hex) => { setTempThemeColor(hex) }}
  textColor={tempTextColor}
  setTextColor={(hex) => { setTempTextColor(hex) }}
  reportTitle="Course Tracker Report"
  onGenerate={async () => {
                if (!selectedBranchId) {
                  notify(
                    "Please select a branch first.",
                    "warning"
                  );
                  return;
                }

                /* Open the tab synchronously so the browser does not block it. */
                const reportWindow = window.open(
                  "about:blank",
                  "_blank"
                );

                if (reportWindow) {
                  reportWindow.document.title =
                    "Generating Course Tracker Report...";
                  reportWindow.document.body.innerHTML = `
                    <div style="font-family:Arial,sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;color:#555;">
                      Generating Course Tracker Report...
                    </div>`;
                }

                updatePdfThemeColor(
                  tempThemeColor
                );

                updatePdfTextColor(
                  tempTextColor
                );

                setColorOpen(false);

                await generatePDF(
                  tempThemeColor,
                  tempTextColor,
                  reportWindow
                );
              }}
  loading={pdfLoading}
  disabled={pdfLoading ||
                !selectedBranchId}
/>

      {/* =====================================================
          SNACKBAR
      ====================================================== */}

      <Snackbar
        open={
          snackbar.open
        }
        autoHideDuration={
          4500
        }
        onClose={() =>
          setSnackbar(
            (state) => ({
              ...state,
              open: false,
            })
          )
        }
        anchorOrigin={{
          vertical:
            "bottom",

          horizontal:
            "right",
        }}
      >
        <Alert
          severity={
            snackbar.severity
          }
          variant="filled"
          onClose={() =>
            setSnackbar(
              (state) => ({
                ...state,
                open: false,
              })
            )
          }
        >
          {
            snackbar.message
          }
        </Alert>
      </Snackbar>
    </Box>
  );
}